import React, { useEffect, useState } from "react";
import "../styleSheets/billingExpense.css";

function BillingDashboard() {
  const token = localStorage.getItem("token");
  const [total, setTotal] = useState(0);
  const [overAll, setOverAll] = useState(0);
  const [entries, setEntries] = useState([]);
  const [cycleStart, setCycleStart] = useState("");
  const [cycleEnd, setCycleEnd] = useState("");
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [cycleLoading, setCycleLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [form, setForm] = useState({
    fType: "swiggyfood",
    amount: "",
    billDate: "",
    note: "",
  });

  const LIMIT = 15000;
  const percentage = Math.min((total / LIMIT) * 100, 100);

  let progressColor = "bg-success";
  if (percentage >= 80 && percentage < 100) progressColor = "bg-warning";
  if (percentage >= 100) progressColor = "bg-danger";

  /* ---------------- CYCLE GENERATOR ---------------- */

  const generateCycles = (count = 6) => {
    const today = new Date();
    const cycles = [];

    for (let i = 0; i < count; i++) {
      const ref = new Date(today.getFullYear(), today.getMonth() - i, 1);

      const start = new Date(ref.getFullYear(), ref.getMonth() - 1, 20);
      const end = new Date(ref.getFullYear(), ref.getMonth(), 18, 23, 59, 59);

      const label = `${start.toLocaleString("default", {
        month: "short",
      })} 20 - ${end.toLocaleString("default", {
        month: "short",
      })} 18`;

      cycles.push({
        label,
        start: start.toISOString(),
        end: end.toISOString(),
      });
    }

    return cycles;
  };

  const [cycles] = useState(generateCycles());

  /* ---------------- FETCH FUNCTION ---------------- */

  const fetchCycleData = async (cycle = null, isInitial = false) => {
    try {
      if (isInitial) setPageLoading(true);
      else setCycleLoading(true);

      let url = `${process.env.REACT_APP_API_URL}/api/billing/cycle-data`;

      if (cycle) {
        url += `?start=${cycle.start}&end=${cycle.end}`;
      }

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      setTotal(data.total || 0);
      setOverAll(data.overAll || 0);
      setEntries(data.entries || []);
      setCycleStart(new Date(data.start).toDateString());
      setCycleEnd(new Date(data.end).toDateString());
    } catch (err) {
      console.error(err);
    } finally {
      if (isInitial) setPageLoading(false);
      else setCycleLoading(false);
    }
  };

  /* ---------------- INITIAL LOAD ---------------- */

  useEffect(() => {
    fetchCycleData(null, true);
  }, []);

  /* ---------------- DROPDOWN CHANGE ---------------- */

  const handleChange = (e) => {
    const cycle = cycles[e.target.value];
    setSelectedCycle(cycle);
    fetchCycleData(cycle);
  };

  /* ---------------- FORM HANDLING ---------------- */

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const addManualEntry = async (e) => {
    e.preventDefault();
    if (submitLoading) return;

    if (!form.amount || !form.billDate) {
      alert("Please fill all required fields");
      return;
    }

    try {
      setSubmitLoading(true);

      await fetch(
        editingId
          ? `${process.env.REACT_APP_API_URL}/api/billing/editExpense/${editingId}`
          : `${process.env.REACT_APP_API_URL}/api/billing/manual-add`,
        {
          method: editingId ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(form),
        },
      );

      setForm({
        fType: "swiggyfood",
        amount: "",
        billDate: "",
        note: "",
      });

      if (selectedCycle) {
        await fetchCycleData(selectedCycle);
      } else {
        await fetchCycleData();
      }
      setEditingId(null);
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitLoading(false);
    }
  };

  /* ---------------- PAGE LOADER ---------------- */

  if (pageLoading) {
    return (
      <div className="page-loader">
        <div className="spinner-border text-primary"></div>
      </div>
    );
  }

  const eligibleTotal = entries.reduce(
    (sum, e) => sum + (e.amount >= 249 ? e.amount : 0),
    0,
  );

  const nonEligibleTotal = entries.reduce(
    (sum, e) => sum + (e.amount < 249 ? e.amount : 0),
    0,
  );

  const handleEdit = (entry) => {
    setEditingId(entry._id);

    setForm({
      amount: entry.amount,
      note: entry.note || "",
      billDate: new Date(entry.billDate).toISOString().split("T")[0],
      fType: entry.fType,
    });

    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: "smooth",
    });
  };

  const eligibleOrders = entries.filter((e) => e.amount >= 249).length;

  const smallOrders = entries.filter((e) => e.amount < 249).length;

  const CASHBACK_LIMIT = 1500;

  // Total cashback pool: 10% of eligible spend, capped at ₹1,500
  const cashbackEarned = Math.min(
    Math.floor(eligibleTotal * 0.1),
    CASHBACK_LIMIT,
  );

  const eligiblePercentage = Math.min((eligibleTotal / LIMIT) * 100, 100);

  // All commissions earned by Santosh
  const totalCommissions = entries.reduce((sum, e) => {
    return sum + (Number(e.transaction?.tCommission) || 0);
  }, 0);

  // Allocate cashback in date order so each eligible order receives
  // only the cashback remaining under the overall ₹1,500 limit.
  const sortedEntries = [...entries].sort(
    (a, b) => new Date(a.billDate) - new Date(b.billDate),
  );

  let remainingCashback = CASHBACK_LIMIT;
  let totalManualCashback = 0;

  for (const e of sortedEntries) {
    const amount = Number(e.amount) || 0;

    if (amount < 249) continue;

    const allocatedCashback = Math.min(amount * 0.1, remainingCashback);

    // Every eligible order uses part of the shared cashback pool.
    remainingCashback -= allocatedCashback;

    // Only eligible manual Swiggy Food and Instamart orders
    // contribute manual cashback to Santosh's earnings.
    if (
      e.source === "manual" &&
      ["swiggyfood", "instamart"].includes(e.fType)
    ) {
      totalManualCashback += allocatedCashback;
    }
  }

  // Santosh gets commissions plus allocated manual cashback.
  // His earnings cannot exceed the total cashback pool.
  const cappedTotalEarnings = Math.min(
    totalCommissions + totalManualCashback,
    cashbackEarned,
  );

  /* ---------------- UI ---------------- */

  return (
    <div className="billing-container">
      {/* ================= SUMMARY ================= */}

      <div className="billing-card billing-summary">
        <div className="summary-header">
          <div>
            <h3>Cashback Tracker</h3>

            <p
              style={{
                marginTop: "4px",
                color: "#666",
                fontSize: "14px",
              }}
            >
              Only orders ₹249+ are eligible for cashback
            </p>
          </div>

          <span className="cycle-range">
            {cycleStart} → {cycleEnd}
          </span>
        </div>

        <div className="summary-amount">
          ₹ {eligibleTotal}
          <span> / ₹ {LIMIT}</span>
        </div>

        <div className="progress billing-progress">
          <div
            className={`progress-bar billing-bar ${
              eligiblePercentage >= 100
                ? "danger-bar"
                : eligiblePercentage >= 80
                  ? "warning-bar"
                  : "safe-bar"
            }`}
            style={{
              "--progress": `${eligiblePercentage}%`,
            }}
          >
            {eligiblePercentage.toFixed(1)}%
          </div>
        </div>

        <p
          style={{
            marginTop: "12px",
            fontSize: "15px",
            color: "#444",
          }}
        >
          ₹ {LIMIT - eligibleTotal} eligible spend remaining
        </p>

        {/* ===== STATS ===== */}

        <div className="stats-grid">
          <div className="stat-box">
            <span>Eligible Spend</span>
            <h4>₹ {eligibleTotal}</h4>
          </div>
          <div className="stat-box">
            <span>Non Eligible</span>
            <h4>₹ {nonEligibleTotal}</h4>
          </div>

          <div className="stat-box">
            <span>Total Spend</span>
            <h4>₹ {overAll}</h4>
          </div>
          <div className="stat-box">
            <span>Cashback</span>
            <h4>₹ {cashbackEarned}</h4>
          </div>
          <div className="stat-box">
            <span>Eligible Orders</span>
            <h4>{eligibleOrders}</h4>
          </div>

          <div className="stat-box">
            <span>Small Orders</span>
            <h4>{smallOrders}</h4>
          </div>
        </div>
        <div className="earnings-container">
          <div className="earnings-card vamshi">
            <span>Vamshi's Earnings</span>
            <h4>
              ₹ {parseInt(Math.max(0, cashbackEarned - cappedTotalEarnings))}
            </h4>
          </div>

          <div className="earnings-card santosh">
            <span>Santosh's Earnings</span>
            <h4>₹ {Math.floor(cappedTotalEarnings)}</h4>
          </div>
        </div>
      </div>

      {/* ================= ENTRIES ================= */}

      <div className="billing-card">
        <div className="card-header-row">
          <h5>Orders This Cycle</h5>
        </div>

        {cycleLoading ? (
          <div className="center-spinner">
            <span className="spinner-border spinner-border-sm"></span>
          </div>
        ) : entries.length === 0 ? (
          <p className="empty-text">No entries found</p>
        ) : (
          <div className="table-responsive">
            <table className="table billing-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Source</th>
                  <th>Note</th>
                  <th>Edit</th>
                  {/*<th>Commissions</th>*/}
                </tr>
              </thead>

              <tbody>
                {entries.map((e) => (
                  <tr
                    key={e._id}
                    style={{
                      background: e.amount < 249 ? "#fff5f5" : "",
                    }}
                  >
                    <td>{e.fType}</td>

                    <td className="amount-col">₹ {e.amount}</td>

                    <td>
                      {e.amount >= 249 ? (
                        <span
                          style={{
                            background: "#e8f8ee",
                            color: "#18864b",
                            padding: "6px 10px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          Eligible
                        </span>
                      ) : (
                        <span
                          style={{
                            background: "#ffe8e8",
                            color: "#d62828",
                            padding: "6px 10px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          &lt; ₹249
                        </span>
                      )}
                    </td>

                    <td>
                      {new Date(e.billDate).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "2-digit",
                      })}
                    </td>

                    <td>
                      {e.source === "transaction" ? "Transaction" : "Manual"}
                    </td>

                    <td>
                      {e.source === "transaction" ? "Auto Generated" : e.note}
                    </td>

                    <td>
                      <button
                        className="edit-btn"
                        onClick={() => handleEdit(e)}
                      >
                        <i class="fa-regular fa-pen-to-square"></i>
                      </button>
                    </td>
                    {/* <td>
                      {e.transaction?.tCommission >= 0
                        ? e.transaction?.tCommission
                        : "-"}
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ================= MANUAL ENTRY ================= */}

      <div className="billing-card">
        <h5>Add Manual Entry</h5>

        <form onSubmit={addManualEntry} className="manual-form">
          <div className="form-grid">
            <div>
              <label>Type</label>

              <select
                name="fType"
                className="form-select"
                value={form.fType}
                onChange={onChange}
              >
                <option value="swiggyfood">Swiggy Food</option>

                <option value="instamart">Instamart</option>
              </select>
            </div>

            <div>
              <label>Amount</label>

              <input
                type="number"
                name="amount"
                className="form-control"
                value={form.amount}
                onChange={onChange}
              />
            </div>

            <div>
              <label>Date</label>

              <input
                type="date"
                name="billDate"
                className="form-control"
                value={form.billDate}
                onChange={onChange}
              />
            </div>

            <div>
              <label>Note</label>

              <input
                type="text"
                name="note"
                className="form-control"
                value={form.note}
                onChange={onChange}
              />
            </div>
          </div>

          <button className="btn add-entry-btn" disabled={submitLoading}>
            {submitLoading ? (
              <span className="spinner-border spinner-border-sm"></span>
            ) : editingId ? (
              "Update Entry"
            ) : (
              "Add Entry"
            )}
          </button>
        </form>
      </div>

      {/* ================= MONTHLY ================= */}

      <div className="billing-card monthly-card">
        <h5>Monthly Billing</h5>

        <select
          className="form-select mb-3"
          onChange={handleChange}
          disabled={cycleLoading}
        >
          <option value="">Select Cycle</option>

          {cycles.map((c, index) => (
            <option key={index} value={index}>
              {c.label}
            </option>
          ))}
        </select>

        {cycleLoading && (
          <div className="center-spinner">
            <span className="spinner-border spinner-border-sm"></span>
          </div>
        )}

        <div className="monthly-total">
          <span>Eligible Spend</span>

          <h2>₹ {eligibleTotal}</h2>
        </div>
      </div>
    </div>
  );
}

export default BillingDashboard;
